import React, { useEffect, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { TextField } from "@components/FormsV2";
import Select2V2 from "@components/FormsV2/Select2/Select2V2";
import CountryPicker from "@components/Forms/CountryPicker";
import DropdownApiJs from "@components/FormsV2/DropdownApiJs/DropdownApiJs";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import NestedItemSelect, {
  Section,
} from "@components/NestedItemSelect/NestedItemSelect";
import useGetProject from "../CreateBlueprintFromProjectModal/api/useGetProject";
import { buildPayload } from "../CreateBlueprintFromProjectModal/projectSections";
import { buildSections } from "./projectSections";
import useFlags from "@common/hooks/useFlags";
import {
  useV2CreateProjectDuplicationMutation,
  useV2GetAccountQuery,
  useV2GetProjectByIdQuery,
  useV2ListBusinessUnitsForAccountQuery,
  useV2ListClientsForAccountQuery,
  useV2ListPaymentTermsForAccountQuery,
  useV2ListPresalesEngineersForAccountQuery,
  useV2ListProjectLocationsQuery,
  useV2ListRateTablesForAccountQuery,
  useV2ListSalesExecutivesForAccountQuery,
} from "@generated";
import AutoCompleteText from "@components/Forms/AutoCompleteText/AutoCompleteText";
import { withErrorReason } from "@components/CatalogIndex/saveErrorToast";
import "./style.css";

interface Props {
  projectId: string;
  show: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

// Duplication runs in a background job, so a successful response only means the
// request was accepted — not that the new project exists yet.
export const DUPLICATE_PROJECT_QUEUED_MESSAGE =
  "Duplication request submitted. The new project will appear in your project list in a moment.";

const EMPTY_FORM = {
  crmOption: null as any,
  projectName: "",
  salesExecutiveId: "",
  presalesEngineerId: "",
  serviceLocation: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  country: "" as any,
  paymentTermId: "",
  rateTableId: "",
  businessUnitId: "",
};

const EMPTY_CLIENT = { id: null as string | null, name: "" };

const DEFAULT_FIELD_LABELS: Record<string, string> = {
  presales_engineer: "Pre-Sales Engineer",
  sales_executive: "Sales Executive",
  business_unit: "Business Unit",
  location: "Service Location",
  client: "Client",
  crm_opportunity: "CRM Opportunity",
  request_approval: "Request Approval",
};

interface OptionItem {
  value: string;
  label: string;
}

// Loose record shape for list/include data read at this boundary; the
// generated v2 types omit some attributes we read (e.g. `active`).
interface ApiRecord {
  id?: string;
  type?: string;
  attributes?: Record<string, any>;
  relationships?: Record<string, { data?: { id?: string } | unknown }>;
}

const toOptions = (records: ApiRecord[] | undefined): OptionItem[] =>
  (records ?? []).map((record) => {
    const { name } = record.attributes ?? {};
    return { value: String(record.id ?? ""), label: name || "" };
  });

const toOneId = (record: ApiRecord | undefined, key: string): string => {
  const data = record?.relationships?.[key]?.data as
    | { id?: string }
    | null
    | undefined;
  return data?.id != null ? String(data.id) : "";
};

// Only the to-one relationships step 1 pre-fills; the step-2 section data
// comes from useGetProject's heavier request, so step 1 is not held up by it.
const PROJECT_DETAIL_INCLUDES: (
  | "client"
  | "sales-executive"
  | "presales-engineer"
  | "business-unit"
  | "payment-term"
  | "rate-table"
)[] = [
  "client",
  "sales-executive",
  "presales-engineer",
  "business-unit",
  "payment-term",
  "rate-table",
];

const DuplicateProjectModal: React.FC<Props> = ({
  projectId,
  show,
  onClose,
  onSuccess,
}) => {
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;

  const { data: accountData, isLoading: accountLoading } = useV2GetAccountQuery(
    {}
  );
  // Mounted only while open, so refetch on each mount: a reopen must not
  // pre-fill from a cached, pre-edit project.
  const { data: projectDetails, isFetching: projectFetching } =
    useV2GetProjectByIdQuery(
      { id: projectId, include: PROJECT_DETAIL_INCLUDES },
      { refetchOnMountOrArgChange: true }
    );
  const { data: salesExecsData, isLoading: salesExecsLoading } =
    useV2ListSalesExecutivesForAccountQuery({ page: { size: 500 } });
  const { data: presalesData, isLoading: presalesLoading } =
    useV2ListPresalesEngineersForAccountQuery({
      filter: { active: "true,false" as unknown as boolean },
      page: { size: 500 },
    });
  const { data: paymentTermsData, isLoading: paymentTermsLoading } =
    useV2ListPaymentTermsForAccountQuery({});
  const { data: rateTablesData, isLoading: rateTablesLoading } =
    useV2ListRateTablesForAccountQuery({ page: { size: 500 } });
  const { data: businessUnitsData, isLoading: businessUnitsLoading } =
    useV2ListBusinessUnitsForAccountQuery({});
  const { data: locationsData, isFetching: locationsFetching } =
    useV2ListProjectLocationsQuery(
      { filter: { project: projectId } },
      { refetchOnMountOrArgChange: true }
    );

  // The project and its locations gate on isFetching (not isLoading) so the
  // step-1 pre-fill waits for their refetch instead of using cached data.
  const isLoading =
    accountLoading ||
    projectFetching ||
    salesExecsLoading ||
    presalesLoading ||
    paymentTermsLoading ||
    rateTablesLoading ||
    businessUnitsLoading ||
    locationsFetching;

  const { "crm?": crm, "field-labels": accountFieldLabels } = (accountData?.data
    ?.attributes ?? {}) as {
    "crm?"?: boolean;
    "field-labels"?: Record<string, string>;
  };
  const isCrm = crm ?? false;
  const fieldLabels: Record<string, string> =
    accountFieldLabels ?? DEFAULT_FIELD_LABELS;

  const projectData = projectDetails?.data as ApiRecord | undefined;
  const includedData = (projectDetails?.included ?? []) as ApiRecord[];
  const { "project-name": sourceProjectName, status: projectStatus } =
    projectData?.attributes ?? {};
  const includedOf = (type: string, id: string) =>
    id
      ? includedData.find((item) => item.type === type && item.id === id)
      : undefined;
  const clientId = toOneId(projectData, "client");
  const includedClient = includedOf("clients", clientId);
  const rateTableId = toOneId(projectData, "rate-table");
  const includedRateTable = includedOf("rate-tables", rateTableId);

  const projectLocations = (locationsData?.data ?? []) as ApiRecord[];

  const salesExecOptions = toOptions(salesExecsData?.data as ApiRecord[]);

  // When building, show only active engineers; otherwise show all so
  // inactive/departed engineers still appear on past projects.
  const presalesRecords = (presalesData?.data ?? []) as ApiRecord[];
  const presalesOptions = toOptions(
    projectStatus === "building"
      ? presalesRecords.filter((pe) => pe.attributes?.active)
      : presalesRecords
  );

  const paymentTermOptions = toOptions(paymentTermsData?.data as ApiRecord[]);

  // Active rate tables, plus the project's current one even if inactive.
  const activeRateTables = ((rateTablesData?.data ?? []) as ApiRecord[]).filter(
    (rt) => rt.attributes?.active
  );
  if (
    includedRateTable &&
    !activeRateTables.find((rt) => rt.id === includedRateTable.id)
  ) {
    activeRateTables.push(includedRateTable);
  }
  const rateTableOptions = toOptions(activeRateTables);

  const businessUnitOptions = toOptions(businessUnitsData?.data as ApiRecord[]);

  const {
    configCrmOpportunityRequired,
    configSalesExecutiveRequired,
    configPresalesEngineerRequired,
    configBusinessUnitRequired,
    configServiceLocationRequired,
  } = useFlags();

  const { project, isFetching: isProjectItemsLoading } =
    useGetProject(projectId);
  const [duplicateProject, { isLoading: isSubmitting }] =
    useV2CreateProjectDuplicationMutation();

  const labels = {
    crmOpportunity: fieldLabels["crm_opportunity"] || "CRM Opportunity",
    salesExecutive: fieldLabels["sales_executive"] || "Sales Executive",
    presalesEngineer: fieldLabels["presales_engineer"] || "Pre-Sales Engineer",
    client: fieldLabels["client"] || "Client",
    location: fieldLabels["location"] || "Service Location",
    paymentTerm: fieldLabels["payment_term"] || "Payment Term",
    businessUnit: fieldLabels["business_unit"] || "Business Unit",
  };

  const [selectedClient, setSelectedClient] = useState({ ...EMPTY_CLIENT });
  const { data: clientsData } = useV2ListClientsForAccountQuery({
    filter: { active: true, name: selectedClient.name },
  });
  const clients = ((clientsData?.data ?? []) as ApiRecord[]).map((c) => {
    const { name } = c.attributes ?? {};
    return { id: String(c.id), name: (name ?? "") as string };
  });
  const clientList = clients.map((c) => c.name);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [sections, setSections] = useState<Section[]>(() =>
    buildSections(null)
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">(
    "success"
  );

  // These effects also run on mount, so a modal mounted already open
  // (show=true) — with its data cached or not — still pre-fills step 1 and
  // seeds the step-2 sections once the data arrives.
  const hasProjectData = !!projectData;
  useEffect(() => {
    if (show && !isLoading && projectData) {
      const {
        name: locName,
        street,
        street2,
        city,
        state,
        "postal-code": postalCode,
        country,
      } = projectLocations[0]?.attributes ?? {};
      const { name: clientName } = includedClient?.attributes ?? {};
      setSelectedClient({
        id: includedClient?.id ? String(includedClient.id) : null,
        name: clientName ?? "",
      });
      setForm({
        crmOption: null,
        projectName: sourceProjectName ?? "",
        salesExecutiveId: toOneId(projectData, "sales-executive"),
        presalesEngineerId: toOneId(projectData, "presales-engineer"),
        serviceLocation: locName ?? "",
        addressLine1: street ?? "",
        addressLine2: street2 ?? "",
        city: city ?? "",
        state: state ?? "",
        zip: postalCode ?? "",
        country: country ?? "",
        paymentTermId: toOneId(projectData, "payment-term"),
        rateTableId: includedRateTable?.id ? String(includedRateTable.id) : "",
        businessUnitId: toOneId(projectData, "business-unit"),
      });
    }
  }, [show, isLoading, hasProjectData]);

  const hasProject = !!project;
  useEffect(() => {
    if (show && !isProjectItemsLoading && project)
      setSections(buildSections(project));
  }, [show, isProjectItemsLoading, hasProject]);

  const set = (key: keyof typeof EMPTY_FORM) => (val: any) =>
    setForm((f) => ({ ...f, [key]: val }));

  const handleContinue = () => {
    const missing: string[] = [];
    if (isCrm && configCrmOpportunityRequired && !form.crmOption)
      missing.push(labels.crmOpportunity);
    if (!form.projectName.trim()) missing.push("Project Name");
    if (configSalesExecutiveRequired && !form.salesExecutiveId)
      missing.push(labels.salesExecutive);
    if (configPresalesEngineerRequired && !form.presalesEngineerId)
      missing.push(labels.presalesEngineer);
    if (!selectedClient.name.trim()) missing.push(labels.client);
    if (configServiceLocationRequired && !form.serviceLocation.trim())
      missing.push(labels.location);
    if (!form.paymentTermId) missing.push(labels.paymentTerm);
    if (!form.rateTableId) missing.push("Rate Table");
    if (configBusinessUnitRequired && !form.businessUnitId)
      missing.push(labels.businessUnit);
    if (missing.length > 0) {
      setAlertVariant("danger");
      setAlertMessage(`Required fields missing: ${missing.join(", ")}`);
      return;
    }
    setAlertMessage(null);
    setStep(2);
  };

  const handleSubmit = async () => {
    /* This buildPayload function is also used in CreateBlueprintFromProjectModal and creates 
    an array of JSON API records from the sections in Step 2. The additional logic that comes after this
    function is for defining the project attributes/relationships specific to Duplicating Project (Step 1) 
    that are not present in CreateBlueprintFromProject */
    const payload = buildPayload(sections, project);

    // Ensure the project resource exists at the front of the payload
    let projectResource = payload.find((item: any) => item.type === "projects");
    if (!projectResource) {
      projectResource = { type: "projects", id: Number(projectId) };
      payload.unshift(projectResource);
    }

    // Merge step 1 form attributes
    projectResource.attributes = {
      ...(projectResource.attributes ?? {}),
      "project-name": form.projectName,
      "client-name": selectedClient.name,
    };

    // Merge step 1 form relationships
    const formRelationships: Record<string, any> = {};
    for (const [rel, id] of [
      ["sales-executive", form.salesExecutiveId],
      ["presales-engineer", form.presalesEngineerId],
      ["payment-term", form.paymentTermId],
      ["rate-table", form.rateTableId],
      ["business-unit", form.businessUnitId],
      ["client", selectedClient.id || ""],
    ]) {
      if (id)
        formRelationships[rel] = { data: { id: Number(id), type: `${rel}s` } };
    }
    if (form.crmOption?.value && form.crmOption.value !== -1)
      formRelationships["crm-opportunity"] = {
        data: { id: Number(form.crmOption.value), type: "crm-opportunities" },
      };

    projectResource.relationships = {
      ...(projectResource.relationships ?? {}),
      ...formRelationships,
    };

    // Add project-location record to payload
    if (form.serviceLocation || form.addressLine1 || form.city) {
      const existingLocationId = projectLocations[0]?.id;
      const locationRecord: any = {
        type: "project-locations",
        attributes: {
          name: form.serviceLocation,
          street: form.addressLine1,
          street2: form.addressLine2,
          city: form.city,
          state: form.state,
          "postal-code": form.zip,
          country: form.country,
        },
      };
      if (existingLocationId) locationRecord.id = Number(existingLocationId);
      payload.push(locationRecord);
    }

    try {
      const data = {
        type: "project-duplications",
        attributes: { content: payload },
        relationships: {
          "source-project": {
            data: { id: projectId, type: "projects" },
          },
        },
      };
      const response = await duplicateProject({
        projectId: projectId,
        body: { data },
      }).unwrap();
      if (response?.data?.id) {
        handleClose();
        onSuccess(DUPLICATE_PROJECT_QUEUED_MESSAGE);
      }
    } catch (e) {
      setAlertVariant("danger");
      setAlertMessage(withErrorReason("Failed to duplicate project.", e));
    }
  };

  const handleClose = () => {
    setStep(1);
    setForm({ ...EMPTY_FORM });
    setSelectedClient({ ...EMPTY_CLIENT });
    setSections(buildSections(null));
    setAlertMessage(null);
    onClose();
  };

  const renderStep1 = () => (
    <>
      <div className="sectionHeader">1. Project Details</div>
      <div className="description">
        Enter the details relevant to your organization in regards to the
        project.
      </div>
      {isCrm && (
        <Row>
          <Col sm={12}>
            <DropdownApiJs
              value={form.crmOption}
              setValue={set("crmOption")}
              placeholder={`Select ${labels.crmOpportunity}`}
              defaultValue={{ value: -1, label: "-- None --" }}
              onChange={set("crmOption")}
              onInputChange={null}
              required={configCrmOpportunityRequired}
              label={`${configCrmOpportunityRequired ? "*" : ""}${
                labels.crmOpportunity
              }`}
              className=""
              isInvalid={false}
              onFocus={null}
              url={`${apiHost}/v2/crm-opportunities`}
              isDisabled={false}
              filter="display-name"
              blankOption={{ value: -1, label: "-- None --" }}
              canCreate={false}
              setDataState={null}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={12}>
          <TextField
            id="projectName"
            label="*Project Name"
            placeholder="Enter Project Name"
            value={form.projectName}
            onChange={(e) => set("projectName")(e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <Select2V2
            label={`${configSalesExecutiveRequired ? "*" : ""}${
              labels.salesExecutive
            }`}
            placeholder={`Select ${labels.salesExecutive}`}
            value={
              salesExecOptions.find(
                (o) => String(o.value) === form.salesExecutiveId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("salesExecutiveId")(opt?.value ? String(opt.value) : "")
            }
            options={salesExecOptions}
            isClearable={!configSalesExecutiveRequired}
          />
        </Col>
        <Col sm={6}>
          <Select2V2
            label={`${configPresalesEngineerRequired ? "*" : ""}${
              labels.presalesEngineer
            }`}
            placeholder={`Select ${labels.presalesEngineer}`}
            value={
              presalesOptions.find(
                (o) => String(o.value) === form.presalesEngineerId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("presalesEngineerId")(opt?.value ? String(opt.value) : "")
            }
            options={presalesOptions}
            isClearable={!configPresalesEngineerRequired}
          />
        </Col>
      </Row>

      <div className="sectionHeader">2. Client Details</div>
      <div className="description">
        Enter the details relevant to the client for whom you are rendering
        services.
      </div>
      <Row>
        <Col sm={12}>
          <AutoCompleteText
            required
            id="clientName"
            label={`*${labels.client}`}
            placeholder={`Enter ${labels.client}`}
            suggestionList={clientList}
            value={selectedClient.name}
            monitorValue={(value: string) => {
              const match = clients.find((c) => c.name === value);
              setSelectedClient({ id: match ? match.id : null, name: value });
            }}
            setTextChanged={() => {}}
            crmId={null}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <TextField
            id="serviceLocation"
            label={`${configServiceLocationRequired ? "*" : ""}${
              labels.location
            }`}
            placeholder={`Enter ${labels.location}`}
            value={form.serviceLocation}
            onChange={(e) => set("serviceLocation")(e.target.value)}
          />
        </Col>
      </Row>
      <Form.Group className="formGroup">
        <Form.Label>Address</Form.Label>
        <TextField
          id="addressLine1"
          label={null}
          placeholder="Address line 1"
          value={form.addressLine1}
          onChange={(e) => set("addressLine1")(e.target.value)}
        />
        <TextField
          id="addressLine2"
          label={null}
          placeholder="Address line 2"
          value={form.addressLine2}
          onChange={(e) => set("addressLine2")(e.target.value)}
        />
      </Form.Group>
      <Row>
        <Col sm={3}>
          <TextField
            id="city"
            label={null}
            placeholder="City"
            value={form.city}
            onChange={(e) => set("city")(e.target.value)}
          />
        </Col>
        <Col sm={3}>
          <TextField
            id="state"
            label={null}
            placeholder="State / Prov / Terr"
            value={form.state}
            onChange={(e) => set("state")(e.target.value)}
          />
        </Col>
        <Col sm={3}>
          <TextField
            id="zip"
            label={null}
            placeholder="Zip / Post Code"
            value={form.zip}
            onChange={(e) => set("zip")(e.target.value)}
          />
        </Col>
        <Col sm={3}>
          <CountryPicker
            country={form.country}
            onCountryChange={set("country")}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <Select2V2
            label={`*${labels.paymentTerm}`}
            placeholder={`Select ${labels.paymentTerm}`}
            value={
              paymentTermOptions.find(
                (o) => String(o.value) === form.paymentTermId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("paymentTermId")(opt?.value ? String(opt.value) : "")
            }
            options={paymentTermOptions}
          />
        </Col>
        <Col sm={6}>
          <Select2V2
            label="*Rate Table"
            placeholder="Select Rate Table"
            value={
              rateTableOptions.find(
                (o) => String(o.value) === form.rateTableId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("rateTableId")(opt?.value ? String(opt.value) : "")
            }
            options={rateTableOptions}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <Select2V2
            label={`${configBusinessUnitRequired ? "*" : ""}${
              labels.businessUnit
            }`}
            placeholder={`Select ${labels.businessUnit}`}
            value={
              businessUnitOptions.find(
                (o) => String(o.value) === form.businessUnitId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("businessUnitId")(opt?.value ? String(opt.value) : "")
            }
            options={businessUnitOptions}
            isClearable={!configBusinessUnitRequired}
          />
        </Col>
      </Row>
    </>
  );

  const renderStep2 = () =>
    isProjectItemsLoading ? (
      <ScopeStackSpinner />
    ) : (
      <>
        <div className="sectionHeader">3. Include Project Sections</div>
        <div className="description">
          Choose which portions of this project to include in duplicated
          project.
        </div>
        <NestedItemSelect sections={sections} onSectionsChange={setSections} />
      </>
    );

  return (
    <>
      {alertMessage && (
        <ToastAlert
          variant={alertVariant}
          text={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
      <ScopeStackModal
        className="duplicateProjectModal"
        show={show}
        modalTitle="Duplicate Project"
        modalBody={
          isLoading ? (
            <ScopeStackSpinner />
          ) : step === 1 ? (
            renderStep1()
          ) : (
            renderStep2()
          )
        }
        button1Text="Cancel"
        button2Text={
          step === 1
            ? "Continue"
            : isSubmitting
            ? "Duplicating..."
            : "Duplicate Project"
        }
        handleClose={handleClose}
        handleButton1Click={handleClose}
        handleButton2Click={step === 1 ? handleContinue : handleSubmit}
        button2Disabled={isSubmitting}
      />
    </>
  );
};

export default DuplicateProjectModal;
